import { useEffect, useState } from 'react';
import { t } from '@lingui/macro';

import Panel from '../../page-framework/panel/PanelDecorator';
import { useId, useIsLoading } from '../../page-framework/panel/Panel';
import { PanelEditingActions } from '../../page-framework/panel/PanelActions';
import { panelPropTypes, panelPropDefaults } from '../../page-framework/panel/PanelPropTypes';

import BusinessServicesOverviewStatusBreakout from './BusinessServicesOverviewStatusBreakout';
import BusinessServicesOverviewMatrix from './BusinessServicesOverviewMatrix';
import { useTimeRangeSelection } from '../../charts/status-breakout/UseTimeRangeSelection';
import { FilterStateContainer } from '../../../stores/FilterStateStore';
import {
    MATRIX_PANEL_PREVIEW_IMAGE,
    PANEL_CATEGORIES,
    PANEL_PRIORITIES,
} from '../../page-framework/panel/PanelRegistry';
import { businessServiceGroupFilterId, statusFilterId, timeRangeFilterId } from '../../filters';
import { useDataService } from '../../../services/ApiDataService';
import MatrixDisplayOptionsAction, { useStatusTrendVisibility } from '../../matrix/internal/MatrixDisplayOptionsAction';

const title = t`Business Services by Site`;

function BusinessServicesOverviewPanel({ flexHeight }) {
    const id = useId();
    const [, /* Not used */ setIsLoading] = useIsLoading();
    const [timeRangeSelection, setTimeRangeSelection] = useState(undefined);
    const showStatusTrend = useStatusTrendVisibility();
    const matrixFilterState = useTimeRangeSelection(timeRangeSelection, showStatusTrend);
    const dataService = useDataService();

    useEffect(() => {
        // The status trend will set the panel's loading flag when it loads data.  We need to be sure that
        // the loading flag gets cleared when the status trend is hidden.  Otherwise, the loading indicator
        // will be displayed indefinitely if the status trend was in the process of loading data when it
        // was hidden.
        if (!showStatusTrend) setIsLoading(false);
    }, [showStatusTrend, setIsLoading]);

    return (
        <>
            <PanelEditingActions>
                <MatrixDisplayOptionsAction />
            </PanelEditingActions>
            {showStatusTrend && <BusinessServicesOverviewStatusBreakout onTimeRangeChange={setTimeRangeSelection} />}
            {!!matrixFilterState && (
                <FilterStateContainer
                    name={`${id}-matrix-filter-state`}
                    dataService={dataService}
                    manageUrl={false}
                    state={matrixFilterState}
                >
                    <BusinessServicesOverviewMatrix visibleRows={flexHeight ? 0 : 10} />
                </FilterStateContainer>
            )}
        </>
    );
}

BusinessServicesOverviewPanel.propTypes = panelPropTypes;

BusinessServicesOverviewPanel.defaultProps = {
    ...panelPropDefaults,
    title,
    autoHeight: true,
};

export default Panel({
    typeAlias: 'Matrix',
    catalogEntry: {
        category: PANEL_CATEGORIES.BUSINESS_SERVICE,
        priority: PANEL_PRIORITIES.OVERVIEW,
        title,
        description: t`Status matrix for business services by sites`,
        previewImage: MATRIX_PANEL_PREVIEW_IMAGE,
    },
    filters: {
        required: [timeRangeFilterId],
        optional: [businessServiceGroupFilterId, statusFilterId],
    },
})(BusinessServicesOverviewPanel);
